import React from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { useThemeMode } from "flowbite-react";

const CumulativePLGraph = ({ data, totalPL, darkMode }) => {
  let cumulativePL = 0;
  const chartData = [{ day: 0, pl: 0 }]; // Start with an initial point at (0,0)

  const sortedDays = Object.keys(data)
    .map(day => parseInt(day))
    .sort((a, b) => a - b);

  for (let day = 1; day <= 31; day++) {
    if (sortedDays.includes(day)) {
      cumulativePL += data[day].pl || 0;
    }
    chartData.push({
      day: day,
      pl: Number(cumulativePL.toFixed(2)), // Round to 2 decimal places
    });
  }

  const maxPL = Math.max(...chartData.map((item) => item.pl));
  const minPL = Math.min(...chartData.map((item) => item.pl));
  const absMax = Math.max(Math.abs(maxPL), Math.abs(minPL));
  const yAxisDomain = [Math.floor(minPL / 50) * 50, Math.ceil(absMax / 50) * 50];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const pl = payload[0].value;
      const color = pl >= 0 ? "#22c55e" : "#ef4444";
      return (
        <div className={`p-2 bg-white border border-slate-500 rounded shadow`}>
          <p className="text-sm">Day: {label}</p>
          <p className="text-sm" style={{ color }}>
            P&L: £{pl.toFixed(2)}
          </p>
        </div>
      );
    }
    return null;
  };

  const formatYAxis = (value) => `£${value}`;

  return (
    <ResponsiveContainer width="100%" height={150}>
      <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke={darkMode ? "#666666" : "#e5e7eb"} />
        <Line type="monotone" dataKey="pl" stroke={totalPL ? "#ef4444" : "#22c55e"} dot={false} />
        <XAxis
          dataKey="day"
          stroke={darkMode ? "#64748b" : "#000000"}
          tick={true}
          tickCount={31}
          domain={[0, 31]}
          style={{
            fontSize: "0.6rem",
          }}
          allowDataOverflow={true}
          scale="linear"
          type="number"
        />
        <YAxis
          stroke={darkMode ? "#64748b" : "#000000"}
          tick={true}
          style={{
            fontSize: "0.6rem",
          }}
          domain={yAxisDomain}
          ticks={Array.from({ length: (yAxisDomain[1] - yAxisDomain[0]) / 50 + 1 }, (_, i) => yAxisDomain[0] + i * 50)}
          tickFormatter={formatYAxis}
        />
        <Tooltip content={<CustomTooltip />} />
      </LineChart>
    </ResponsiveContainer>
  );
};

const WinLossChart = ({ data, darkMode }) => {
  const winningTrades = Object.values(data).filter((day) => day.pl > 0).length;
  const losingTrades = Object.values(data).filter((day) => day.pl < 0).length;
  const chartData = [
    { name: "", value: winningTrades },
    { name: "", value: losingTrades },
  ];
  const COLORS = ["#22c55e", "#ef4444"];

  return (
    <ResponsiveContainer width="100%" height={150}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={35}
          outerRadius={50}
          fill="#8884d8"
          dataKey="value"
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          style={{ fontSize: "0.6rem" }}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
};

const MonthlySummary = ({ summary, monthlyData }) => {
  const { totalPL, totalTrades } = summary;

  const { mode } = useThemeMode();
  const darkMode = mode === "dark";

  return (
    <div className="p-4 mb-4 rounded-lg dark:bg-slate-800 text-slate-400 bg-white">
      <div className="flex flex-wrap -mx-2">
        <div className="w-full md:w-1/4 px-2">
          <h3 className="text mb-2">Monthly Summary</h3>
          <p className={`text-lg ${totalPL >= 0 ? "text-green-500" : "text-red-500"}`}>Total P/L: £{totalPL.toFixed(2)}</p>
          <p className="text-lg mb-4">Number of Trades: {totalTrades}</p>
        </div>
        <div className="w-full md:w-1/2 px-2">
          <h3 className="text mb-2">Cumulative P&L</h3>
          <CumulativePLGraph data={monthlyData} darkMode={darkMode} totalPL={totalPL} />
        </div>
        <div className="w-full md:w-1/4 px-2">
          <h3 className="text mb-2">Winning vs Losing</h3>
          <WinLossChart data={monthlyData} darkMode={darkMode} />
        </div>
      </div>
    </div>
  );
};

export default MonthlySummary;
